import { get, post } from './client';
import type { Team } from '@/types/api';

// Team settings response derived from Team object
export interface TeamSettingsResponse {
  logo_url: string | null;
  okendo_user_id: string | null;
  slack_channel: string | null;
  shopify_domain: string | null;
}

// Get team details (settings are part of team object)
export async function getTeamSettings(teamId: string): Promise<Team> {
  // Team object contains all settings - use teams list endpoint
  const teams = await get<Team[]>('/api/teams');
  const team = teams.find(t => t.id === teamId);
  if (!team) throw new Error('Team not found');
  return team;
}

// Update team logo
export async function updateTeamLogo(
  teamId: string,
  logoUrl: string
): Promise<Team> {
  return post<Team>(`/api/teams/${teamId}/logo`, {
    logo_url: logoUrl,
  });
}

// Okendo configuration
export interface OkendoConfig {
  api_key?: string;
  user_id?: string;
}

export async function saveOkendoConfig(
  teamId: string,
  config: OkendoConfig
): Promise<{ success: boolean; message: string }> {
  // Okendo config is saved via team API key endpoint
  return post<{ success: boolean; message: string }>(
    `/api/teams/${teamId}/api-key`,
    {
      okendo_api_key: config.api_key,
      okendo_user_id: config.user_id,
    }
  );
}

// Slack configuration
export interface SlackConfig {
  slack_workspace_id?: string;
  workspace_name?: string;
  bot_token?: string;
  channel_id?: string;
  channel_label?: string;
}

export async function saveSlackConfig(
  teamId: string,
  config: SlackConfig
): Promise<{ success: boolean; message: string }> {
  return post<{ success: boolean; message: string }>(
    `/api/teams/${teamId}/slack`,
    {
      slack_workspace_id: config.slack_workspace_id,
      workspace_name: config.workspace_name,
      bot_token: config.bot_token,
      channel_id: config.channel_id,
      channel_label: config.channel_label,
    }
  );
}

// Shopify configuration
export interface ShopifyConfig {
  store_domain?: string;
  access_token?: string;
}

export async function saveShopifyConfig(
  teamId: string,
  config: ShopifyConfig
): Promise<{ success: boolean; message: string }> {
  return post<{ success: boolean; message: string }>(
    `/api/teams/${teamId}/shopify/api`,
    {
      shopify_store_url: config.store_domain,
      shopify_access_token: config.access_token,
    }
  );
}

// Gorgias configuration
export interface GorgiasConfig {
  api_key?: string;
  email?: string;
  base_url?: string;
}

export async function saveGorgiasConfig(
  teamId: string,
  config: GorgiasConfig
): Promise<{ gorgias_configured: boolean }> {
  return post<{ gorgias_configured: boolean }>(
    `/api/teams/${teamId}/gorgias/credentials`,
    {
      api_key: config.api_key,
      email: config.email,
      base_url: config.base_url,
    }
  );
}

// Flip CX configuration
export interface FlipConfig {
  api_key?: string;
}

export async function saveFlipConfig(
  teamId: string,
  config: FlipConfig
): Promise<{ flip_configured: boolean }> {
  return post<{ flip_configured: boolean }>(
    `/api/teams/${teamId}/flip/credentials`,
    { api_key: config.api_key }
  );
}

// Sage Supply Chain Intelligence configuration
export interface SageConfig {
  api_key?: string;
  team_id?: string;
  base_url?: string;
}

export interface SageResponse {
  sage_configured: boolean;
  sage_base_url: string | null;
  sage_team_id: string | null;
  sage_last_synced_at: string | null;
}

export async function saveSageConfig(
  teamId: string,
  config: SageConfig
): Promise<SageResponse> {
  return post<SageResponse>(
    `/api/teams/${teamId}/sage/credentials`,
    config
  );
}

export interface SageTestResponse {
  status: string;
  connected: boolean;
  sage_team_id: string;
}

export async function testSageConnection(
  teamId: string
): Promise<SageTestResponse> {
  return get<SageTestResponse>(`/api/teams/${teamId}/sage/test`);
}

// Invite team member
export async function inviteTeamMember(
  teamId: string,
  email: string,
  role: string = 'member'
): Promise<{ success: boolean; message: string }> {
  return post<{ success: boolean; message: string }>(
    `/api/teams/${teamId}/invite`,
    { email, role }
  );
}

// BigQuery configuration
export interface BigQueryConfig {
  project_id?: string;
  credentials_json?: string;
  dataset?: string;
  tables?: string[];
  exec_pulse_finance_dataset?: string;
  exec_pulse_finance_table?: string;
  exec_pulse_finance_date_column?: string;
  exec_pulse_finance_metric_map?: Record<string, string>;
  clear_credentials?: boolean;
}

export interface BigQueryResponse {
  bigquery_configured: boolean;
  bigquery_project_id: string | null;
  bigquery_dataset: string | null;
  bigquery_last_synced_at: string | null;
  bigquery_tables: string[] | null;
  exec_pulse_finance_dataset: string | null;
  exec_pulse_finance_table: string | null;
  exec_pulse_finance_date_column: string | null;
  exec_pulse_finance_metric_map: Record<string, string> | null;
}

export interface BigQueryTablesResponse {
  tables: string[];
  selected: string[];
  datasets?: string[];
}

export async function listBigQueryTables(
  teamId: string
): Promise<BigQueryTablesResponse> {
  return get<BigQueryTablesResponse>(`/api/teams/${teamId}/bigquery/tables`);
}

export interface BigQueryDatasetsResponse {
  datasets: string[];
}

export interface BigQueryDatasetTablesResponse {
  dataset: string;
  tables: string[];
}

export interface ExecPulseFinanceSchemaResponse {
  dataset: string;
  table: string;
  date_column: string | null;
  columns: string[];
  mapped_metrics: Record<string, string>;
  missing_metrics: string[];
}

export async function listBigQueryDatasets(
  teamId: string
): Promise<BigQueryDatasetsResponse> {
  return get<BigQueryDatasetsResponse>(`/api/teams/${teamId}/bigquery/datasets`);
}

export async function listBigQueryDatasetTables(
  teamId: string,
  dataset: string
): Promise<BigQueryDatasetTablesResponse> {
  return get<BigQueryDatasetTablesResponse>(
    `/api/teams/${teamId}/bigquery/datasets/${encodeURIComponent(dataset)}/tables`
  );
}

export async function getExecPulseFinanceSchema(
  teamId: string
): Promise<ExecPulseFinanceSchemaResponse> {
  return get<ExecPulseFinanceSchemaResponse>(
    `/api/teams/${teamId}/bigquery/exec-pulse-finance-schema`
  );
}

export async function saveBigQueryConfig(
  teamId: string,
  config: BigQueryConfig
): Promise<BigQueryResponse> {
  return post<BigQueryResponse>(
    `/api/teams/${teamId}/bigquery/credentials`,
    config
  );
}

export interface BigQueryTestResponse {
  success: boolean;
  message: string;
  datasets_found?: number;
  sample_datasets?: string[];
}

export async function testBigQueryConnection(
  teamId: string
): Promise<BigQueryTestResponse> {
  return get<BigQueryTestResponse>(`/api/teams/${teamId}/bigquery/test`);
}

// AI Persona configuration
export interface RipplingConfig {
  api_key?: string;
  company_id?: string;
}

export async function saveRipplingConfig(
  teamId: string,
  config: RipplingConfig
): Promise<{ rippling_configured: boolean }> {
  return post<{ rippling_configured: boolean }>(
    `/api/teams/${teamId}/rippling/credentials`,
    config
  );
}

export interface TripleWhaleConfig {
  api_key?: string;
}

export async function saveTripleWhaleConfig(
  teamId: string,
  config: TripleWhaleConfig
): Promise<{ triplewhale_configured: boolean; triplewhale_api_key_preview?: string | null }> {
  return post<{ triplewhale_configured: boolean; triplewhale_api_key_preview?: string | null }>(
    `/api/teams/${teamId}/triplewhale/credentials`,
    config
  );
}

export interface RampConfig {
  api_key?: string;
  client_id?: string;
  client_secret?: string;
}

export async function saveRampConfig(
  teamId: string,
  config: RampConfig
): Promise<{
  ramp_configured: boolean;
  ramp_api_key_preview?: string | null;
  ramp_client_id_preview?: string | null;
  ramp_client_secret_preview?: string | null;
}> {
  return post<{
    ramp_configured: boolean;
    ramp_api_key_preview?: string | null;
    ramp_client_id_preview?: string | null;
    ramp_client_secret_preview?: string | null;
  }>(
    `/api/teams/${teamId}/ramp/credentials`,
    config
  );
}

export interface AiPersonaConfig {
  name?: string;
  avatar_url?: string;
  persona?: string;
  best_practices?: string;
}

export async function saveAiPersona(
  teamId: string,
  config: AiPersonaConfig
): Promise<{ success: boolean }> {
  return post<{ success: boolean }>(
    `/api/teams/${teamId}/assistant/persona`,
    config
  );
}
